import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useAttributes, useCategoryTree, useDeletePublicationCatalogRule, usePublicationCatalogRules } from '../../hooks';
import { flattenCategoryTree } from '../../lib/categoryTree';
import { describeRuleCriteria } from '../../lib/publicationRuleCriteria';
import type { CriteriaDescribeContext } from '../../lib/publicationRuleCriteria';
import { useToast } from '../../lib/useToast';
import {
  PUBLICATION_CRITERIA_KIND_LABELS,
  PUBLICATION_RULE_DIRECTION_LABELS,
  publicationRuleDirectionColor,
} from '../../model/options';
import type { PublicationCatalogRule } from '../../model/publicationTypes';
import { PublicationRuleFormDialog } from './PublicationRuleFormDialog';

export function PublicationRulesPanel({ catalogId, canManage }: { catalogId: string; canManage: boolean }) {
  const { t, i18n } = useTranslation('catalog');
  const rulesQuery = usePublicationCatalogRules(catalogId);
  const categoryTreeQuery = useCategoryTree();
  const attributesQuery = useAttributes();
  const deleteRule = useDeletePublicationCatalogRule();
  const { toast, success, error: notifyError, clear } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PublicationCatalogRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PublicationCatalogRule | null>(null);

  const describeCtx: CriteriaDescribeContext = useMemo(
    () => ({
      categoryNameById: new Map(flattenCategoryTree(categoryTreeQuery.data ?? []).map((n) => [n.id, n.name])),
      attributeDictionary: attributesQuery.data ?? [],
      locale: i18n.language,
    }),
    [categoryTreeQuery.data, attributesQuery.data, i18n.language],
  );

  if (rulesQuery.isLoading) {
    return <LoadingState label={t('publicationRulesPanel.loading')} />;
  }
  if (rulesQuery.isError) {
    return <ErrorState message={rulesQuery.error?.message} onRetry={() => void rulesQuery.refetch()} />;
  }

  const rules = rulesQuery.data ?? [];

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteRule.mutate(
      { catalogId, ruleId: deleteTarget.id },
      {
        onSuccess: () => {
          success(t('publicationRulesPanel.deleted'));
          setDeleteTarget(null);
        },
        onError: (error) => {
          notifyError(error.message);
          setDeleteTarget(null);
        },
      },
    );
  };

  return (
    <Box>
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        {t('publicationRulesPanel.excludeWinsHint')}
      </Alert>

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        {canManage ? (
          <Button
            startIcon={<AddRoundedIcon />}
            variant="outlined"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            {t('publicationRulesPanel.addRule')}
          </Button>
        ) : null}
      </Stack>

      {rules.length === 0 ? (
        <EmptyState
          title={t('publicationRulesPanel.empty.title')}
          description={t('publicationRulesPanel.empty.description')}
        />
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('publicationRulesPanel.table.direction')}</TableCell>
                <TableCell>{t('publicationRulesPanel.table.kind')}</TableCell>
                <TableCell>{t('publicationRulesPanel.table.criteria')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.id} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      color={publicationRuleDirectionColor(rule.direction)}
                      label={t(PUBLICATION_RULE_DIRECTION_LABELS[rule.direction])}
                    />
                  </TableCell>
                  <TableCell>{t(PUBLICATION_CRITERIA_KIND_LABELS[rule.criteriaKind])}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{describeRuleCriteria(describeCtx, rule, t)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {canManage ? (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t('common:action.edit')}>
                          <IconButton
                            size="small"
                            aria-label={t('publicationRulesPanel.table.editAria')}
                            onClick={() => {
                              setEditing(rule);
                              setFormOpen(true);
                            }}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common:action.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={t('publicationRulesPanel.table.deleteAria')}
                            onClick={() => setDeleteTarget(rule)}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <PublicationRuleFormDialog
        open={formOpen}
        catalogId={catalogId}
        rule={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onResult={(severity, message) => (severity === 'success' ? success(message) : notifyError(message))}
      />

      <Dialog open={deleteTarget !== null} onClose={() => (!deleteRule.isPending ? setDeleteTarget(null) : undefined)}>
        <DialogTitle>{t('publicationRulesPanel.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('publicationRulesPanel.deleteDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteRule.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteRule.isPending}
            startIcon={deleteRule.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}
